import React, { useMemo } from "react";
import moment from "moment";
import { useHeaderActions } from "../../../hooks/useHeaderActions";
import { useMembershipFeesDetail } from "./hooks/useMembershipFeesDetail";
import Header from "../../../components/Header";
import { Table } from "../../../../../shared/Table/Table";
import { Box } from "../../../../../shared/Box/Box";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import { LoadMoreObserver } from "../../../../../shared/LoadMoreObserver/LoadMoreObserver";
import classes from "../../../newBusinessInsightsContent.module.scss";
import { formatCurrency } from "../../../../../utilities/general";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";
import { uiNotification } from "../../../../../services/UINotificationService";
import { tCommon } from "../../../../../i18n/useAppTranslation";

const MembershipFeesDetail = () => {
  const { selectedClinic, period, apiPeriod } = useHeaderActions();

  // Prepare API parameters
  const requestParams = useMemo(() => {
    const params = {
      ...apiPeriod,
    };

    if (selectedClinic?.value?.value !== 0) {
      params["clinic_ids[]"] = selectedClinic.value.value;
    }

    return params;
  }, [apiPeriod, selectedClinic]);

  // Fetch data
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useMembershipFeesDetail(requestParams, {
    staleTime: 0,
    cacheTime: 0,
    keepPreviousData: true,
    onError: () => {
      uiNotification.error(tCommon("error.getReports"));
    },
  });

  // Prepare download params (remove pagination for export)
  const downloadParams = useMemo(() => {
    const params = { ...requestParams };
    delete params.page;
    delete params.limit;
    return params;
  }, [requestParams]);

  // Extract data from paginated structure
  const reportData = useMemo(() => {
    return data?.pages?.map((page) => page.report).flat() || [];
  }, [data]);

  const apiClinics = useMemo(() => {
    return data?.pages?.[0]?.clinics || [];
  }, [data]);

  const tableColumns = [
    {
      data: "Patient Name",
      accessor: "patientName",
    },
    {
      data: "Email",
      accessor: "email",
    },
    {
      data: "Membership Type",
      accessor: "membershipType",
    },
    {
      data: "Fee Amount",
      accessor: "feeAmount",
    },
    {
      data: "Payment Date",
      accessor: "paymentDate",
    },
    {
      data: "Invoice #",
      accessor: "invoiceNumber",
    },
    {
      data: "Provider",
      accessor: "providerName",
    },
    {
      data: "Status",
      accessor: "status",
    },
  ];

  const tableData = useMemo(() => {
    return reportData.map((item) => ({
      key: item.invoice_id,
      patientName: item.patient_name || "-",
      email: item.patient_email || "-",
      membershipType: item.membership_type?.replace("_", " ") || "-",
      feeAmount: formatCurrency(item.net_membership_fee || 0),
      paymentDate: item.payment_datetime
        ? moment(item.payment_datetime).format("MM/DD/YYYY")
        : "-",
      invoiceNumber: item.invoice_number || "-",
      providerName: item.provider_name || "-",
      status: item.invoice_status || "-",
    }));
  }, [reportData]);

  const clinics = useMemo(() => {
    const clinicOptions = [{ label: "All Clinics", value: 0 }];
    if (apiClinics?.length) {
      clinicOptions.push(
        ...apiClinics.map((clinic) => ({
          label: clinic.clinic_name,
          value: clinic.id,
        })),
      );
    }
    return clinicOptions;
  }, [apiClinics]);

  return (
    <div className="flex-col gap-16">
      <Header
        title="Membership Fees Detail"
        clinics={clinics}
        selectedClinic={selectedClinic}
        period={period}
        downloadRoute={HTTP_ENDPOINTS.getMembershipFeesDetail()}
        requestParams={downloadParams}
      />

      <Box>
        {isLoading && (
          <div className={classes.tableContainer}>
            <Skeleton count={10} height={40} borderRadius="0px" />
          </div>
        )}

        {error && (
          <Empty position="center">
            {`API Error: Failed to load membership fees data: ${
              error.message || "Unknown error"
            }`}
          </Empty>
        )}

        {!isLoading && !error && tableData.length > 0 && (
          <div className={classes.tableContainer} style={{ overflowX: "auto" }}>
            <Table
              cols={tableColumns}
              data={tableData}
              colClassName="bordersides"
              headColClassName={classes.tableHeaderColumn}
            />
            {hasNextPage && (
              <LoadMoreObserver
                noPaddingBottom
                loadMore={fetchNextPage}
                isLoading={isFetchingNextPage}
                hasMore={hasNextPage}
              />
            )}
          </div>
        )}

        {!isLoading && !error && tableData.length === 0 && (
          <Empty position="center">
            No membership fees data found: Try adjusting your date range or
            clinic selection
          </Empty>
        )}
      </Box>
    </div>
  );
};

export default MembershipFeesDetail;
